import { useEffect, useState } from "react";

import { formatUsd } from "../../shared/money";
import { DEFAULT_THEME_COLOR } from "../../shared/theme";
import type { PublicPress, PublicState } from "../../shared/types";
import { CheckoutDialog } from "../components/CheckoutDialog";
import { CurrentHolder } from "../components/CurrentHolder";
import { LoadingState } from "../components/LoadingState";
import { PressButton } from "../components/PressButton";
import { Stats } from "../components/Stats";
import { getHistory } from "../lib/api";

interface HomePageProps {
  state: PublicState | null;
  error: string | null;
  refresh: (signal?: AbortSignal) => Promise<void>;
}

export function HomePage({ state, error, refresh }: HomePageProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [recent, setRecent] = useState<PublicPress[]>([]);

  useEffect(() => {
    const controller = new AbortController();
    void getHistory(1, 5, controller.signal)
      .then((history) => setRecent(history.items))
      .catch(() => setRecent([]));
    return () => controller.abort();
  }, [state?.version]);

  if (!state) {
    return (
      <main className="centered-page">
        <LoadingState />
        {error ? <p className="form-error">{error}</p> : null}
      </main>
    );
  }

  if (state.maintenanceMode) {
    return (
      <main className="centered-page maintenance-page">
        <p className="section-kicker">DON'T PRESS.</p>
        <h1>Seriously.</h1>
        <p>The button is currently under maintenance.</p>
      </main>
    );
  }

  const currentThemeColor = state.currentHolder?.themeColor ?? DEFAULT_THEME_COLOR;

  return (
    <main>
      {state.paymentsPaused ? (
        <div className="system-banner" role="status">
          <strong>THE BUTTON IS TEMPORARILY DISABLED.</strong>
          <span>No payments can be started right now.</span>
        </div>
      ) : null}

      <section className="control-room" aria-label="Current holder and press control">
        <CurrentHolder holder={state.currentHolder} />
        <aside className="control-panel" aria-label="Press the button">
          <PressButton
            minimumPriceCents={state.currentPriceCents}
            disabled={state.paymentsPaused}
            loading={false}
            onPress={() => setDialogOpen(true)}
          />
          <dl className="control-ledger">
            <div>
              <dt>CURRENT HOLDER PAID</dt>
              <dd>
                {state.currentHolder
                  ? formatUsd(state.currentHolder.amountSubtotalCents)
                  : "NOTHING YET"}
              </dd>
            </div>
            <div>
              <dt>MINIMUM STEP</dt>
              <dd>+{formatUsd(state.incrementCents)}</dd>
            </div>
            <div>
              <dt>SITE COLOR</dt>
              <dd className="theme-color-value">
                <span style={{ backgroundColor: currentThemeColor }} aria-hidden="true" />
                {currentThemeColor.toUpperCase()}
              </dd>
            </div>
          </dl>
        </aside>
      </section>

      <Stats state={state} />

      <section className="price-mechanic" id="how-it-works" aria-labelledby="mechanic-title">
        <div>
          <h2 id="mechanic-title">Choose the minimum or go higher.</h2>
          <p>
            Pay the displayed minimum or more. Your captured subtotal sets the benchmark, then $1 is
            added. While you hold, your color controls the entire site.
          </p>
        </div>
        <ol className="mechanic-ledger">
          <li>
            <span>Current minimum</span>
            <strong>{formatUsd(state.currentPriceCents)}</strong>
          </li>
          <li>
            <span>You choose</span>
            <strong>{formatUsd(state.currentPriceCents)} or more</strong>
          </li>
          <li>
            <span>Next minimum</span>
            <strong>Your amount + $1</strong>
          </li>
        </ol>
        <p className="mechanic-note">
          This is not an auction. The first valid authorization to claim the current version wins,
          not the highest amount.
        </p>
      </section>

      <section className="history-preview" aria-labelledby="history-title">
        <div className="section-heading">
          <h2 id="history-title">THE PRESS HISTORY</h2>
          <a href="/history">VIEW ALL</a>
        </div>
        {recent.length === 0 ? (
          <div className="history-empty">
            <p>No successful presses.</p>
            <span>Production starts clean. There is no founder entry and no fake activity.</span>
          </div>
        ) : (
          <ol className="history-list">
            {recent.map((press) => (
              <li key={press.pressNumber}>
                <a href={`/press/${press.pressNumber}`}>
                  <span>#{press.pressNumber}</span>
                  <strong>{press.displayName}</strong>
                  <span>{formatUsd(press.amountSubtotalCents)}</span>
                  <span>{press.isCurrentHolder ? "CURRENT" : "HISTORY"}</span>
                </a>
              </li>
            ))}
          </ol>
        )}
      </section>

      {dialogOpen ? (
        <CheckoutDialog
          state={state}
          onClose={() => setDialogOpen(false)}
          onStale={() => void refresh()}
        />
      ) : null}
    </main>
  );
}
